import CustomAmplitudeClient from "./custom-amplitude-client.b75422c9d833.js";
import {
  InsuranceSelectorChangedEvent,
  InsuranceSelectorPlanChosenEventType,
  InsuranceSelectorDialogPlanSelectedEventType,
  InsuranceSelectorDialogMemberIdConfirmedEventType,
  InsuranceSelectorDialogNoPlanSelectedEventType,
  InsuranceSelectorDialogPlanResetEventType,
  OpenInsuranceSelectorEvent,
} from "./insurance-selector-events.382c4178547f.js";
import { FloatingLabel } from "./floating-label.895f0e243516.js";

/**
 * Custom element <insurance-selector> for displaying and selecting insurance plans.
 *
 * Expects child elements:
 * - .tqc-click-target: The clickable area to open dialog
 * - #insurance-selector-toggle: Text element displaying plan name
 * - #insurance-selector-member-id .tqc-insurance-member-id-value: Member ID display (optional)
 * - input[name=plan][type=hidden]: Hidden input storing selected plan ID
 *
 * Events emitted:
 * - "tqc:insurance-selector:open" - When user clicks to open insurance selector dialog
 *   detail: { navMethod: "insurance-selector" }
 * - "tqc:insurance-selector:changed" - When plan selection changes
 *   detail: { fullName, planId, coverageType, memberId?, planDisplayName? }
 *
 * Events listened:
 * - "tqc:insurance-selector:plan-chosen" - Plan selected without member ID
 *   detail: { fullName, planId, coverageType }
 * - "tqc-plan-selected" - Plan selected in dialog (dispatched by server via HTMX HX-Trigger)
 *   detail: { fullName, planId }
 * - "tqc-no-plan-selected" - No plan/cash option selected
 *   detail: { fullName, planId }
 * - "tqc-plan-reset" - Plan selection reset
 *   detail: { fullName, planId }
 * - "tqc-benefits-confirmed" - Member ID confirmed (dispatched by server via HTMX HX-Trigger)
 *   detail: { planDisplayName, planId, memberId }
 *
 * DOM mutations:
 * - Updates #insurance-selector-toggle innerText with selected plan name
 * - Updates .tqc-insurance-member-id-value innerText with member ID
 * - Updates input[name=plan] value with selected plan ID
 * - Adds/removes member-id attribute on <insurance-selector> element
 */
class InsuranceSelector extends HTMLElement {
  constructor() {
    super();

    this.parent = this.getRootNode() || document;
    this.config = JSON.parse(
      this.parent.querySelector("#page-config").textContent,
    );
    if (!this.config) throw new Error("unable to load page config");

    this.elements = {
      toggle: this.querySelector(".tqc-click-target"),
      nameDisplay: this.querySelector("#insurance-selector-toggle"),
      memberIdDisplay: this.querySelector(
        "#insurance-selector-member-id .tqc-insurance-member-id-value",
      ),
      hiddenPlanInput: this.querySelector("input[name=plan][type=hidden]"),
    };
    this.amplitudeClient = new CustomAmplitudeClient(this.config.analyticsUrl);
    this.floatingLabel = this.closest(".tqc-floating-label-group")
      ? new FloatingLabel(this)
      : null;

    this.bindToggleEvents();
  }

  connectedCallback() {
    this.parent.addEventListener(
      InsuranceSelectorPlanChosenEventType,
      this.handlePlanChosen,
    );
    this.parent.addEventListener(
      InsuranceSelectorDialogNoPlanSelectedEventType,
      this.handlePlanChosen,
    );
    this.parent.addEventListener(
      InsuranceSelectorDialogPlanResetEventType,
      this.handlePlanReset,
    );
    this.parent.addEventListener(
      InsuranceSelectorDialogPlanSelectedEventType,
      this.handlePlanChosen,
    );
    this.parent.addEventListener(
      InsuranceSelectorDialogMemberIdConfirmedEventType,
      this.handleMemberIdConfirmed,
    );
  }

  disconnectedCallback() {
    this.parent.removeEventListener(
      InsuranceSelectorPlanChosenEventType,
      this.handlePlanChosen,
    );
    this.parent.removeEventListener(
      InsuranceSelectorDialogNoPlanSelectedEventType,
      this.handlePlanChosen,
    );
    this.parent.removeEventListener(
      InsuranceSelectorDialogPlanResetEventType,
      this.handlePlanReset,
    );
    this.parent.removeEventListener(
      InsuranceSelectorDialogMemberIdConfirmedEventType,
      this.handleMemberIdConfirmed,
    );
  }

  bindToggleEvents() {
    this.elements.toggle.addEventListener("click", this.handleOpen);
    this.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && e.target === this) {
        this.handleOpen();
      }
    });
  }

  handleOpen = () => {
    this.dispatchEvent(
      new OpenInsuranceSelectorEvent(false, {
        navMethod: "insurance-selector",
      }),
    );
  };

  updatePlan(name, value) {
    this.elements.nameDisplay.innerText = name;
    this.elements.hiddenPlanInput.value = value;
    this.floatingLabel?.update(true, false);
  }

  resetPlan(name) {
    this.elements.nameDisplay.innerHTML = `<span class="pv-text-subdued">${name}</span>`;
    this.elements.hiddenPlanInput.value = "";
    this.floatingLabel?.update(false, false);
  }

  handlePlanChosen = (e) => {
    this.updatePlan(e.detail.fullName, e.detail.planId);
    this.removeAttribute("member-id");
    this.dispatchEvent(new InsuranceSelectorChangedEvent(e.detail));
    this.amplitudePlanChosen(e.detail);
  };

  handlePlanReset = (e) => {
    this.resetPlan(e.detail.fullName);
    this.removeAttribute("member-id");
    this.dispatchEvent(new InsuranceSelectorChangedEvent(e.detail));
    this.amplitudePlanChosen(e.detail);
  };

  // triggered by htmx after successfully submitting the insurance form (uses HX-Trigger response header)
  handleMemberIdConfirmed = (e) => {
    this.setAttribute("member-id", "");
    this.updatePlan(e.detail.planDisplayName, e.detail.planId);
    if (this.elements.memberIdDisplay) {
      this.elements.memberIdDisplay.innerText = e.detail.memberId;
    }
    this.dispatchEvent(new InsuranceSelectorChangedEvent(e.detail));
  };

  amplitudePlanChosen(detail) {
    const payload = {
      coverage_type: detail.coverageType,
      plan_full_name: detail.fullName,
      plan_id: detail.plan_id,
    };
    this.amplitudeClient.trackCompleteInsuranceSelection(payload);
  }
}

customElements.define("insurance-selector", InsuranceSelector);
